import React from 'react';
import { Phone, Mail, User, Calendar, Clock, ChevronDown } from 'lucide-react';
import { createThirdPartyInsuranceAppointment } from '../../Services/APIs/patient';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ThirdpartyAppointment = () => {
    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        email: Yup.string().email('Invalid email address').required('Email is required'),
        phoneNumber: Yup.string()
            .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
            .required('Phone number is required'),
        date: Yup.date().required('Date is required').min(new Date(new Date().setDate(new Date().getDate() - 1)), 'Past dates cannot be booked'),
        time: Yup.string().required('Time is required'),
        preferredChannel: Yup.string().required('Preferred channel is required'),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            phoneNumber: '',
            date: '',
            time: '',
            preferredChannel: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                await createThirdPartyInsuranceAppointment(values);
                toast.success('Appointment booked successfully!');
                resetForm();
            } catch (error) {
                if (error.response && error.response.status === 409) {
                    toast.error("Appointment already exists for this patient.");
                } else {
                    console.error('Error creating appointment:', error);
                    toast.error('Failed to book appointment. Please try again.');
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const timeSlots = [
        "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
        "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
        "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM"
    ];

    return (
        <div className="bg-gray-50 min-h-screen flex items-center justify-center">
            <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Book Appointment</h2>
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                name="name"
                                {...formik.getFieldProps('name')}
                                placeholder="Enter your full name"
                                className={`w-full pl-10 pr-3 py-3 border ${formik.touched.name && formik.errors.name ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />
                        </div>
                        {formik.touched.name && formik.errors.name ? (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.name}</div>
                        ) : null}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="email"
                                name="email"
                                {...formik.getFieldProps('email')}
                                placeholder="Enter your email address"
                                className={`w-full pl-10 pr-3 py-3 border ${formik.touched.email && formik.errors.email ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />
                        </div>
                        {formik.touched.email && formik.errors.email ? (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
                        ) : null}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="tel"
                                name="phoneNumber"
                                {...formik.getFieldProps('phoneNumber')}
                                placeholder="Enter your mobile number"
                                className={`w-full pl-10 pr-3 py-3 border ${formik.touched.phoneNumber && formik.errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />
                        </div>
                        {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.phoneNumber}</div>
                        ) : null}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Date <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="date"
                                name="date"
                                {...formik.getFieldProps('date')}
                                min={new Date().toISOString().split('T')[0]}
                                className={`w-full pl-10 pr-3 py-3 border ${formik.touched.date && formik.errors.date ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />
                        </div>
                        {formik.touched.date && formik.errors.date ? (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.date}</div>
                        ) : null}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Time <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <select
                                name="time"
                                {...formik.getFieldProps('time')}
                                disabled={!formik.values.date}
                                className={`w-full pl-10 pr-3 py-3 border ${formik.touched.time && formik.errors.time ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100`}
                            >
                                <option value="" disabled>Select a time slot</option>
                                {timeSlots.map(slot => (
                                    <option key={slot} value={slot}>{slot}</option>
                                ))}
                            </select>
                        </div>
                        {formik.touched.time && formik.errors.time ? (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.time}</div>
                        ) : null}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Preferred Channel <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <select
                                name="preferredChannel"
                                {...formik.getFieldProps('preferredChannel')}
                                className={`w-full appearance-none pl-3 pr-10 py-3 border ${formik.touched.preferredChannel && formik.errors.preferredChannel ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white`}
                            >
                                <option value="" disabled style={{ display: 'none' }}>Select Preferred Channel</option>
                                <option value="in-clinic">In-clinic</option>
                                <option value="video consult">Video Consult</option>
                            </select>
                        </div>
                        {formik.touched.preferredChannel && formik.errors.preferredChannel ? (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.preferredChannel}</div>
                        ) : null}
                    </div>

                    <button type="submit" disabled={!formik.isValid || formik.isSubmitting} className="w-full bg-blue-500 text-white py-3 rounded-xl font-semibold text-lg hover:bg-blue-600 transition-colors disabled:opacity-50">
                        Submit
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ThirdpartyAppointment;
